import { useId } from 'react';
import { Lock, Trash2, Unlock } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { cn } from '@reflex/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import type { AgentEnvVar } from '@/lib/types';
import { ENV_VAR_ROW_FIELD_CLASS } from './field-styles';
import { SecretRefPicker, type AvailableSecretsOwner } from './SecretRefPicker';

export interface AgentEnvVarRowProps {
  entry: AgentEnvVar;
  hasStoredSecrets: boolean;
  onChange: (patch: Partial<AgentEnvVar>) => void;
  onRemove: () => void;
  /** Optional: focus the first relevant input on mount. Useful when the
   *  parent appends a new row in response to user action. */
  autoFocus?: boolean;
  /** Optional: testid suffix (typically the row index) for stable querying. */
  testIdSuffix?: string | number;
  /**
   * Owner context forwarded to the secret picker so user/team-tier
   * rows are scoped to the right identity. Omitted means the picker
   * falls back to the launching user (launch dialog default).
   */
  secretsOwner?: AvailableSecretsOwner;
}

export function AgentEnvVarRow({
  entry,
  hasStoredSecrets,
  onChange,
  onRemove,
  autoFocus = false,
  testIdSuffix,
  secretsOwner,
}: AgentEnvVarRowProps) {
  const inputId = useId();
  const isSecret = Boolean(entry.isSecret);

  return (
    <li
      className="flex items-center gap-1.5"
      data-testid={testIdSuffix !== undefined ? `env-var-row-${testIdSuffix}` : undefined}
      data-secret={isSecret ? 'true' : undefined}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded border',
              isSecret
                ? 'border-status-warning/40 bg-status-warning/10 text-status-warning'
                : 'border-border bg-muted/30 text-muted-foreground',
            )}
            // `img`: a bare span may not carry `aria-label`
            // (axe `aria-prohibited-attr`); this is an icon-only glyph.
            role="img"
            aria-label={isSecret ? 'Secret' : 'Variable'}
          >
            {isSecret ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {isSecret ? 'Secret (stored securely)' : 'Plaintext variable'}
        </TooltipContent>
      </Tooltip>
      <Input
        aria-label={isSecret ? 'Secret env var name' : 'Variable name'}
        id={`${inputId}-key`}
        value={entry.key}
        onChange={(e) => onChange({ key: e.target.value })}
        placeholder="MY_VAR"
        className={ENV_VAR_ROW_FIELD_CLASS}
        autoFocus={autoFocus}
      />
      {isSecret ? (
        <SecretRefPicker
          entry={entry}
          hasStoredSecrets={hasStoredSecrets}
          onChange={onChange}
          owner={secretsOwner}
        />
      ) : (
        <Input
          aria-label="Value"
          id={`${inputId}-value`}
          type="text"
          value={entry.value ?? ''}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="value"
          className={ENV_VAR_ROW_FIELD_CLASS}
          autoComplete="off"
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground"
        aria-label="Remove variable"
        onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
}
